import { computed, inject } from "@angular/core";
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from "@ngrx/signals"
import { EMPTY, exhaustMap, filter, firstValueFrom, map, pipe, switchMap, tap } from "rxjs";
import { MemberPaymentMethodPayload, MemberService } from "../../services/member/member.service";
import { extractErrorMessage } from "../../interceptors/helpers/extract-error-message.helper";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { LoadingStatus } from "../../models/utility/loading-status.model";
import { tapResponse } from "@ngrx/operators";
import { setEntities, withEntities } from "@ngrx/signals/entities";
import { exactLength } from "../../functions/validators";

function extractPaymentMethod(response: any) {
    if (response && Object.prototype.hasOwnProperty.call(response, 'payment_method')) {
        return response.payment_method
    }

    if (response && Object.prototype.hasOwnProperty.call(response, 'paymentMethod')) {
        return response.paymentMethod
    }

    return response
}

export interface Member {
    id: number,
    membership_number: number,
    onboarding_status: number,
    member_state_id: number,
    membership_card_link: string,
    first_name: string,
    last_name: string,
    dob: string,

    email_address: string,
    phone: string,
    address: string
    address2: string
    city: string;
    state: string;
    zip_code: string;

    plan_type: string;

    active: boolean;

    created_date: string;
    updated_date: string;
    end_date: string | null;

    dependents: Dependent[];
    
}
export interface MemberState extends Member{
    loaded: boolean,
    loading: boolean,
    error: {
        error: boolean, message: string
    }
    membershipCardLoadingStatus: LoadingStatus
    memberLoadingStatus: LoadingStatus
    memberPaymentStatus: LoadingStatus
    memberPaymentMethodStatus: LoadingStatus
    submittedPaymentTokens: string[]
    paymentMethod: MemberPaymentMethod
}
export interface Dependent {
    id?: number;
    first_name?: string;
    last_name?: string;
    dob?: string;
    relation: string;
    active?: boolean;
    
}

export type MemberPaymentMethod = Record<string, unknown> | null;


export const MemberStore = signalStore(
    {providedIn: 'root'},
    withState<MemberState>({
        loading: false,
        error: {
            error: false, message: ''
        },
        id: 0,
        membership_number: 0,
        onboarding_status: 0,
        member_state_id: 0,
        membership_card_link: '',
        first_name: '',
        last_name: '',
        dob: '',

        email_address: '',
        phone: '',

        address: '',
        address2: '',
        city: '',
        state: '',
        zip_code: '',

        plan_type: '',

        active: false,

        created_date: '',
        updated_date: '',
        end_date: null,
        loaded: false,
        dependents: [],

        memberLoadingStatus: 'idle',
        membershipCardLoadingStatus: 'idle',
        memberPaymentStatus: 'idle',
        memberPaymentMethodStatus: 'idle',
        submittedPaymentTokens: [],
        paymentMethod: null
    }),
    withEntities<Member>(),
    withComputed((store) => ({
        member_id: computed(() => store.id()),
        member_name : computed(() => store.first_name()+' '+store.last_name()),
        enrollment_date: computed(() => store.created_date()),
        dependents: computed(() => store.dependents())
        
    })),
    withProps((store) => ({
        memberService: inject(MemberService)
    })),
    withMethods((store) => {
        const memberService = inject(MemberService)
        return {
            clear() {
                patchState(store, {
                    loading: false,
                    error: {
                        error: false, message: ''
                    },
                    id: 0,
                    membership_number: 0,
                    onboarding_status: 0,
                    member_state_id: 0,
                    membership_card_link: '',
                    first_name: '',
                    last_name: '',
                    dob: '',

                    email_address: '',
                    phone: '',

                    address: '',
                    address2: '',
                    city: '',
                    state: '',
                    zip_code: '',

                    plan_type: '',

                    active: false,

                    created_date: '',
                    updated_date: '',
                    end_date: null,
                    loaded: false,
                    dependents: [],

                    memberLoadingStatus: 'idle',
                    membershipCardLoadingStatus: 'idle',
                    memberPaymentStatus: 'idle',
                    memberPaymentMethodStatus: 'idle',
                    submittedPaymentTokens: [],
                    paymentMethod: null
                })
            },
            clearError() {
                patchState(store, {
                    error: {
                        error: false,
                        message: ''
                    }
                })
            },
            setError(error: boolean, message: string) {
                patchState(store, {
                    error: {
                        error, message
                    }
                })
            },
            setMemberLoadingStatus(memberLoadingStatus: LoadingStatus) {
                patchState(store, { memberLoadingStatus })
            }, 
            setMemberShipLoadingStatus(membershipCardLoadingStatus: LoadingStatus) {
                patchState(store, { membershipCardLoadingStatus })
            },
            setMemberPaymentStatus(memberPaymentStatus: LoadingStatus) {
                patchState(store, { memberPaymentStatus })
            },
            setMemberPaymentMethodStatus(memberPaymentMethodStatus: LoadingStatus) {
                patchState(store, { memberPaymentMethodStatus })
            },
            setLoading(loading: boolean) {
                patchState(store, { loading })
            },
            setMemberId(id: number) {
                patchState(store, {
                    id
                })
            },
            setMember(member: any) {
                patchState(store, member)
            },
        async getMember(forceReload = false) {
        if (store.loaded() && !forceReload) {
            return;
        }

        this.setLoading(true);
        this.setMemberLoadingStatus('loading');

        try {
            const member = await firstValueFrom(
            store.memberService.getMember().pipe(
                map((response: any) => response.member ?? response)
            )
            );

            patchState(store, {
            ...member,
            dependents: member.dependents ?? [],
            loaded: true,
            memberLoadingStatus: 'success'
            });
        } catch (err) {
            this.setMemberLoadingStatus('error');
            this.setError(true, extractErrorMessage(err));
        } finally {
            this.setLoading(false);
        }
        }
        }
    }),
    withMethods((store) => ({
       onboardMember: rxMethod<{ dto: any; onSuccess?: () => void; onError?: () => void }>(
  pipe(
    tap(() => {
      store.clearError();
    }),
    exhaustMap(({ dto, onSuccess, onError }) => {
      //console.log('Activation API call stopped for backend testing.');
      //console.log(`POST /members/${store.id()}/activate payload:`, dto);

     // store.setMemberLoadingStatus('error');
     //store.setError(true, 'Activation stopped for backend testing. Payload was logged to the console.');
     //onError?.();
     // return EMPTY;

      const paymentToken = String(dto?.payment_token ?? '');

      if (paymentToken && store.submittedPaymentTokens().includes(paymentToken)) {
        store.setMemberLoadingStatus('error');
        store.setError(true, 'This payment has already been submitted. Please wait while we confirm your membership.');
        onError?.();
        return EMPTY;
      }

      store.setMemberLoadingStatus('loading');

      if (paymentToken) {
        patchState(store, {
          submittedPaymentTokens: [...store.submittedPaymentTokens(), paymentToken]
        });
      }

      return store.memberService.submitMember(store.id(), dto).pipe(
        switchMap(() => store.memberService.getMember()),
        tapResponse({
          next: (response: any) => {
            const member = response.member ?? response;

            patchState(store, {
              ...member,
              loaded: true,
              memberLoadingStatus: 'success'
            });

            onSuccess?.();
          },
          error: (err: unknown) => {
            store.setMemberLoadingStatus('error');
            store.setError(true, extractErrorMessage(err));
            onError?.();
          }
        })
      );
    })
  )
 ),

        getAllMembers: rxMethod<void>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberLoadingStatus('loading')
                }),
                exhaustMap(() => 
                    store.memberService.getAllMembers().pipe(
                        tapResponse({
                            next: (response: any) => {
                                store.setMemberLoadingStatus('success')
                                patchState(store, setEntities(response))
                            },
                            error: (error: unknown) => {
                                store.setMemberLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        getMemberById: rxMethod<void>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberLoadingStatus("loading")
                }),
                exhaustMap(() => 
                    store.memberService.getMemberById(store.id()).pipe(
                        tapResponse({
                            next: (member: any) => {
                                store.setMemberLoadingStatus('success')
                                patchState(store, member)
                            },
                            error: (error: unknown) => {
                                store.setMemberLoadingStatus('error')
                            }
                        })
                    )
            
                )
            )
        ),
        addDependent: rxMethod<{dto: any, onSuccess?: () => void}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberLoadingStatus('loading')
                }),
                exhaustMap(({dto, onSuccess}) => 
                    store.memberService.addDependent(store.id(), dto).pipe(
                        tapResponse({
                            next: (response: any) => {
                                store.setMemberLoadingStatus('success')
                                const dependents = store.dependents()
                                dependents.push(response.dependent)
                                patchState(store, {
                                    dependents
                                })
                                if (onSuccess) onSuccess()
                            },
                            error: (error: unknown) => {
                                store.setMemberLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        updateDependent: rxMethod<{dto: any, onSuccess?: () => void}> (
        pipe(
        tap(() => {
        store.clearError();
        store.setMemberLoadingStatus('loading');
        }),
        exhaustMap(({dto, onSuccess}) => {
        if (!dto?.id) {
        store.setMemberLoadingStatus('error');
        store.setError(true, 'Dependent ID is missing. Cannot update dependent.');
        return [];
        }

        return store.memberService.editDependent(store.id(), dto.id, dto).pipe(
        tapResponse({
            next: (response: any) => {
            const dependents = store.dependents().filter((d) => d.id !== dto.id);
            dependents.push(response.member_dependent);

            patchState(store, {
                dependents
            });

            store.setMemberLoadingStatus('success');

            if (onSuccess) onSuccess();
            },
            error: (err: unknown) => {
            store.setMemberLoadingStatus('error');
            store.setError(true, extractErrorMessage(err));
            }
        })
        );
        })
        )
        ),
        removeDependent: rxMethod<{id: number, onSuccess?: () => void}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberLoadingStatus('loading')
                }),
                exhaustMap(({id, onSuccess}) => 
                    store.memberService.deleteDependent(store.id(), id).pipe(
                        tapResponse({
                            next: (response: any) => {
                                store.setMemberLoadingStatus('success')
                                const dependents = store.dependents().filter((d) => d.id !== id)
                                patchState(store, {
                                    dependents
                                })
                                if(onSuccess) onSuccess()
                            },
                            error: (error: unknown) => {
                                store.setMemberLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        updateMemberInformation: rxMethod<any>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberLoadingStatus('loading')
                }),
                exhaustMap((dto) => 
                    store.memberService.updateMember(store.id(), dto).pipe(
                        tapResponse({
                            next: () => {
                                patchState(store, {
                                    ...dto,
                                    memberLoadingStatus: 'success'
                                })                                
                            },
                            error: (error: unknown) => {
                                store.setMemberLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        // submitMemberPayment: rxMethod<any>(
        //     pipe(
        //         tap((dto) => {
        //             store.clearError()
        //             store.setMemberPaymentStatus('loading')
        //         }),
        //         exhaustMap((dto) => 
        //             store.memberService.submitMemberPayment(store.id(), dto).pipe(
        //                 tapResponse({
        //                     next: () => {
        //                         store.setMemberPaymentStatus('success')
        //                     },
        //                     error: () => {
        //                         store.setMemberPaymentStatus('error')
        //                     }
        //                 })
        //             )
        //         )
        //     )
        // ),
        getMemberPaymentMethod: rxMethod<void>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberPaymentMethodStatus('loading')
                }),
                exhaustMap(() =>
                    store.memberService.getMemberPaymentMethod(store.id()).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const paymentMethod = extractPaymentMethod(response)

                                patchState(store, {
                                    paymentMethod,
                                    memberPaymentMethodStatus: 'success'
                                })
                            },
                            error: (err: unknown) => {
                                store.setMemberPaymentMethodStatus('error')
                                store.setError(true, extractErrorMessage(err))
                            }
                        })
                    )
                )
            )
        ),
        updateMemberPaymentMethod: rxMethod<{ dto: MemberPaymentMethodPayload, onSuccess?: () => void }>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberPaymentMethodStatus('loading')
                }),
                exhaustMap(({ dto, onSuccess }) =>
                    store.memberService.updateMemberPaymentMethod(store.id(), dto).pipe(
                        switchMap(() => store.memberService.getMemberPaymentMethod(store.id())),
                        tapResponse({
                            next: (response: any) => {
                                const paymentMethod = extractPaymentMethod(response)

                                patchState(store, {
                                    paymentMethod,
                                    memberPaymentMethodStatus: 'success'
                                })

                                if(onSuccess) onSuccess()
                            },
                            error: (err: unknown) => {
                                store.setMemberPaymentMethodStatus('error')
                                store.setError(true, extractErrorMessage(err))
                            }
                        })
                    )
                )
            )
        ),
        addMemberPaymentMethod: rxMethod<{ dto: MemberPaymentMethodPayload, onSuccess?: () => void }>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberPaymentMethodStatus('loading')
                }),
                exhaustMap(({ dto, onSuccess }) =>
                    store.memberService.addMemberPaymentMethod(store.id(), dto).pipe(
                        switchMap(() => store.memberService.getMemberPaymentMethod(store.id())),
                        tapResponse({
                            next: (response: any) => {
                                const paymentMethod = extractPaymentMethod(response)

                                patchState(store, {
                                    paymentMethod,
                                    memberPaymentMethodStatus: 'success'
                                })

                                if(onSuccess) onSuccess()
                            },
                            error: (err: unknown) => {
                                store.setMemberPaymentMethodStatus('error')
                                store.setError(true, extractErrorMessage(err))
                            }
                        })
                    )
                )
            )
        ),
        deleteMemberPaymentMethod: rxMethod<{ onSuccess?: () => void }>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setMemberPaymentMethodStatus('loading')
                }),
                exhaustMap(({ onSuccess }) =>
                    store.memberService.deleteMemberPaymentMethod(store.id()).pipe(
                        tapResponse({
                            next: () => {
                                patchState(store, {
                                    paymentMethod: null,
                                    memberPaymentMethodStatus: 'success'
                                })

                                if(onSuccess) onSuccess()
                            },
                            error: (err: unknown) => {
                                store.setMemberPaymentMethodStatus('error')
                                store.setError(true, extractErrorMessage(err))
                            }
                        })
                    )
                )
            )
        ),
        getMembershipCard: rxMethod<number>(
            pipe(
                tap(() => {
                    store.setMemberShipLoadingStatus('loading')
                }),
                exhaustMap((member_id) => 
                    store.memberService.getMembershipNumber(member_id).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const { success, filename, presigned_url, message } = response
                                patchState(store, {
                                    membership_card_link: presigned_url
                                })
                                store.setMemberShipLoadingStatus('success')
                            },
                            error: (error: unknown) => {
                                store.setMemberShipLoadingStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        getMemberWithCard: rxMethod<void>(
            pipe(
                tap(() => {
                    store.clearError()
                }),
                filter(() => !store.loaded()),
                tap(() => {
                    store.setLoading(true);
                    store.setMemberLoadingStatus('loading');
                }),
                exhaustMap(() =>
                    store.memberService.getMember().pipe(
                        map((member: any) => member),
                        switchMap((member: any) => {
                            patchState(store, member)
                            patchState(store, { loaded: true})
                            store.setMemberLoadingStatus('success');
                            store.setMemberShipLoadingStatus('loading');

                            return store.memberService.getMembershipNumber(store.id()).pipe(
                                tapResponse({
                                    next: (response: any) => {
                                        const { success, filename, presigned_url, message } = response
                                        patchState(store, {
                                            membership_card_link: presigned_url
                                        })
                                        store.setMemberShipLoadingStatus('success')
                                        store.setLoading(false);
                                    }, 
                                    error: (error: unknown) => {
                                        store.setError(true, extractErrorMessage(error));
                                        store.setMemberLoadingStatus('error');
                                        store.setLoading(false);
                                    }
                                })
                            )
                        }),
                        tapResponse({
                            next: () => {},
                            error: (error: unknown) => {
                                store.setError(true, extractErrorMessage(error));
                                store.setMemberLoadingStatus('error');
                                store.setLoading(false);
                            },
                        })
                    )
                )
            )
        )
    }))
)
